/**
 * AssetOfferLandingPage.jsx
 *
 * Connector-specific landing page. Structurally analogous to AssetOfferLandingPage
 * but:
 *   – fetches its own data (GET_DATASET_LANDING_PAGE by id param)
 *   – renders connector-specific chrome: title bar with PersonAddIcon FAB,
 *     contract/access policy cards, ConnectorAssetPolicy dialog, policy FAB
 *   – then delegates the entire asset layout to the same version-config system
 *     used by AssetOfferLandingPage (useLandingPageData + getLandingPageVersionConfig)
 *
 * Adding a new schema version here = zero changes needed; it's handled by
 * landingPageVersionConfig.jsx exactly as for the regular landing page.
 */

import {useState, useEffect} from "react";
import {useParams, useSearchParams} from "react-router-dom";
import {Box, Container, Typography, Grid, Fab, Tooltip, Link, Stack} from "@mui/material";
import * as jsonld from "jsonld";
import axios from "axios";
import {ReactComponent as PolicyIcon} from "../assets/icons/Shield-Plus-Light--Streamline-Phosphor.svg";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import AssetOfferHeader from "./sharedLandingpageSections/AssetOfferHeader";
import ConnectorAssetPolicy from "./ConnectorAssetPolicy";
import {useLandingPageData} from "./utils/uselandingpagedata";
import {getLandingPageVersionConfig} from "./utils/landingPageVersionConfig";
import {GET_DATASET_LANDING_PAGE, URL_REGISTRY_PARTICIPANT_FORM} from "../config/settings";
import {assetKeys} from "../config/constants";
import { keys } from "../config/nameSpacesKeys";
import {useTranslator} from "../context/TranslatorContext";

export default function AssetOfferLandingPage(props) {
  const {id} = useParams();
  const {language, messages} = useTranslator();
  const defaultLanguage = language || "en";

  // ── connector-specific state ──────────────────────────────────────────────
  const [rawData, setRawData] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [extraInfo, setExtraInfo] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openPolicy, setOpenPolicy] = useState(false);
  const [expanded, setExpanded] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [accessPolicyTitle, setAccessPolicyTitle] = useState("");
  const [contractPolicyLegalCode, setContractPolicyLegalCode] = useState("");
  const [contractPolicyDescription, setContractPolicyDescription] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [searchParams] = useSearchParams();
  const langDcatVersion = searchParams.get("version");

  const contractPolicyTitle = extraInfo?.contractPolicyTitle || "";
  const validPolicy = rawData[assetKeys.hasPolicy] || [];

  // ── fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      let url = GET_DATASET_LANDING_PAGE(id);
      if (language && language !== "en") url += `?lang=${language}`;

      const result = await axios(url);
      setRawData(result.data.dcatDataset);
      setMappings(result.data.mappings);
      setExtraInfo(result.data?.extraInfo);

      const extrInfo = result.data?.extraInfo;
      if (extrInfo) {
        setIsOpen(extrInfo?.open ?? false);
        setAccessPolicyTitle(extrInfo?.accessPolicyTitle || "");
        setContractPolicyLegalCode(extrInfo?.contractPolicyLegalCode || "");
        setContractPolicyDescription(extrInfo?.contractPolicyDescription || "");
        setPayAmount(extrInfo?.payAmount || "");
      }

      jsonld.expand(result.data.dcatDataset).then((response) => {
        setExpanded(response[0][keys.properties][0]);
      });
      setIsLoading(false);
    };
    fetchData();
  }, [id, language]);


  // ── shared data instantiation ─────────────────────────────────────────────
  const data = useLandingPageData({ expanded, mappings, defaultLanguage, langDcatVersion });
  data.defaultLanguage = defaultLanguage;
  data.mappings = mappings;

  // ── loading guard ─────────────────────────────────────────────────────────
  if (isLoading || !expanded || expanded.length === 0) {
    return <div style={{ marginTop: "10%" }}>loading...</div>;
  }

  // ── version config selection ──────────────────────────────────────────────
  // ML models always get v0_9_5 layout; other lrTypes use default.
  // ConnectorOfferLandingPage has no version URL param — lrType alone decides.
  const {Layout} = getLandingPageVersionConfig(langDcatVersion, data.lrType);

  // ── connector-specific chrome ─────────────────────────────────────────────
  const connectorChrome = (
    <Box component="section" sx={{pb: 2}}>
      {/* Title bar */}
      <Grid container justifyContent="space-between" spacing={2} sx={{pb: 2}}>
        <Grid item sm={10}>
          <Typography variant="h2" gutterBottom>
            {data.title.getValue(defaultLanguage)}
          </Typography>
        </Grid>
        <Grid item sm={2}>
          <Tooltip title={messages.accessOfferTootip}>
            <Fab
              onClick={() => window.open(URL_REGISTRY_PARTICIPANT_FORM, "_blank")}
              size="small" variant="extended" color="success"
              style={{width: "100%"}}
            >
              <PersonAddIcon className="nav-icon" style={{marginRight: "0.5em"}} />
              <Typography variant="body2" sx={{color: "#fff"}}>
                {messages.accessOffer}
              </Typography>
            </Fab>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Policy cards */}
      <Grid container justifyContent="space-between" alignItems="stretch" spacing={2} sx={{mb: 2}}>
        {contractPolicyTitle && (
          <Grid item sm={5} sx={{display: "flex"}}>
            <Box className="grey--gradient"
              sx={{borderRadius: "8px", padding: "0.5em", flexGrow: 1,
                   display: "flex", flexDirection: "column", justifyContent: "space-between"}}>
              <Stack direction="column" alignItems="flex-start">
                <Typography variant="subtitle2" color="text.secondary">
                  {messages.catalogue_view_policy_button}
                </Typography>
                <Typography variant="h5">
                  {contractPolicyLegalCode ? (
                    <Link href={contractPolicyLegalCode} target="_blank"
                          rel="noopener noreferrer" underline="hover">
                      {contractPolicyTitle}
                    </Link>
                  ) : contractPolicyTitle}{" "}
                  ({isOpen ? messages.facets_filter_open : messages.all_pages_not_free_of_charge})
                </Typography>
              </Stack>
            </Box>
          </Grid>
        )}

        {accessPolicyTitle && (
          <Grid item sm={5} sx={{display: "flex"}}>
            <Box className="grey--gradient"
              sx={{borderRadius: "8px", padding: "0.5em", flexGrow: 1,
                   display: "flex", flexDirection: "column", justifyContent: "space-between"}}>
              <Stack direction="column" alignItems="flex-start">
                <Typography variant="subtitle2" color="text.secondary">
                  {messages.policy_chip_label_publication}
                </Typography>
                <Typography variant="h5">{accessPolicyTitle}</Typography>
              </Stack>
            </Box>
          </Grid>
        )}

        <Grid item sm={2}>
          <Fab onClick={() => setOpenPolicy(!openPolicy)}
               size="small" variant="extended" color="primary"
               style={{width: "100%"}}>
            <PolicyIcon className="nav-icon" style={{marginRight: "0.5em"}} />
            <Typography variant="body2" sx={{color: "#fff", p: 1}}>
              {messages.catalogue_view_policy_button}
            </Typography>
          </Fab>
          {openPolicy && (
            <ConnectorAssetPolicy
              validPolicy={validPolicy}
              contractPolicyTitle={contractPolicyTitle}
              contractPolicyDescription={contractPolicyDescription}
              contractPolicyLegalCode={contractPolicyLegalCode}
              handleClose={() => setOpenPolicy(false)}
              open={openPolicy}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );

  // ── header (shared with AssetOfferLandingPage) ────────────────────────────
  const headerSlot = (
    <>
      {connectorChrome}
      <AssetOfferHeader
        {...props}
        title={data.title}
        lrType={data.lrType}
        alternative={data.alternative}
        defaultLanguage={defaultLanguage}
        version={data.version}
        mappings={mappings}
        publisher={data.publisher}
        payAmount={payAmount}
        admsIdentifierList={data.admsIdentifierList}
        // v0_9_5 header props
        releaseDate={data.releaseDate}
        modified={data.modified}
        publicItem={data.publicItem}
        externalItem={data.externalItem}
      />
    </>
  );

  return (
    <Container sx={{mt: 5, mb: 8}} maxWidth="lg">
      <Box>
        <Layout data={data} headerSlot={headerSlot} />
      </Box>
    </Container>
  );
}
